import classNames from 'classnames';
import { forwardRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Image from '@alltrails/core/components/Image';
import Typography from '@alltrails/core/components/Typography';
import CounterBadge from '@alltrails/denali/components/CounterBadge';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import ChevronSm from '@alltrails/denali/icons/ChevronSm';
import useLayerCount from '../../hooks/useLayerCount';
import styles from './LayerButton.module.scss';
import waypoints from './waypoints.png';

type Props = {
  isOpen: boolean;
  onClick: (isOpen: boolean) => void;
  /**
   * Always render the compact CTA, regardless of viewport width. Admin apps use this so the layer
   * button stays small even on wide screens where it would otherwise render the full-size card stack.
   */
  forceCompact?: boolean;
};

const LayerButton = forwardRef<HTMLButtonElement, Props>(({ isOpen, onClick, forceCompact = false }, ref) => {
  const intl = useIntl();
  const isCompact = useIsMobileSizedScreen() || forceCompact;
  const count = useLayerCount();

  return (
    <button
      aria-label={intl.formatMessage({ defaultMessage: 'Map layers' })}
      className={classNames(styles.layerButton, { [styles.compact]: forceCompact })}
      ref={ref}
      onClick={() => onClick(!isOpen)}
    >
      {isCompact ? (
        <div className={styles.layerButtonMobileContainer}>
          <Image
            className={styles.layerButtonImage}
            alt={intl.formatMessage({ defaultMessage: 'Base map' })}
            src="https://cdn-assets.alltrails.com/assets/map-layers/v3/map-layer-alltrailsSatellite@2x.png"
            width={56}
            height={56}
          />
          {count > 0 && <CounterBadge className={styles.layerButtonMobileCounterBadge} count={count} color="accent" />}
        </div>
      ) : (
        <>
          <div className={styles.layerButtonTextContainer}>
            <div className={styles.layerButtonText}>
              <Typography variant="text50">
                <FormattedMessage defaultMessage="Map layers" />
              </Typography>
              <ChevronSm orientation="right" />
            </div>
            {count > 0 && <CounterBadge count={count} color="accent" />}
          </div>
          <div className={styles.mapCards}>
            <Image
              className={styles.layerButtonImage}
              alt={intl.formatMessage({ defaultMessage: 'Base map' })}
              src="https://cdn-assets.alltrails.com/assets/map-layers/v3/map-layer-alltrailsSatellite@2x.png"
              width={40}
              height={40}
            />
            <Image
              className={styles.layerButtonImage}
              alt={intl.formatMessage({ defaultMessage: 'Overlays' })}
              src="https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-communityHeatmap@2x.png"
              width={40}
              height={40}
            />
            <Image
              className={styles.layerButtonImage}
              alt={intl.formatMessage({ defaultMessage: 'Extras' })}
              src={waypoints}
              width={40}
              height={40}
            />
          </div>
        </>
      )}
    </button>
  );
});

export default LayerButton;
